import { useEffect, useState } from 'react';

/**
 * Minimal client for /api/v1 (P14 wiring). Session is an HttpOnly cookie; mutations send the CSRF token from /auth/me.
 * Errors keep the server's machine code so pages can show honest states (e.g. META_APP_NOT_CONFIGURED).
 */
export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

let csrfToken = '';
export const setCsrfToken = (t: string) => { csrfToken = t; };

export async function api<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken;
  const res = await fetch(path, { method, credentials: 'include', headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `HTTP_${res.status}`, data?.message ?? `Request failed (${res.status})`);
  return data as T;
}

export interface Me {
  user: { id: string; email: string; displayName: string; isWorkspaceAdmin: boolean };
  csrfToken: string;
  brands: { id: string; name: string; role: 'ADMIN' | 'EDITOR' | 'VIEWER' }[];
}

export type CapabilityState = 'SUPPORTED' | 'UNSUPPORTED' | 'PERMISSION_MISSING' | 'NOT_APPLICABLE' | 'TEMPORARILY_UNAVAILABLE' | 'DEPRECATED' | 'UNKNOWN';
export interface Connection {
  id: string;
  authMode: 'FACEBOOK_LOGIN' | 'INSTAGRAM_LOGIN' | 'SYSTEM_USER';
  status: 'NOT_CONNECTED' | 'CONNECTED' | 'DEGRADED' | 'EXPIRED' | 'REVOKED' | 'DISCONNECTED' | 'ERROR';
  apiVersion: string;
  grantedScopes: string[];
  declinedScopes: string[];
  expiresAt: string | null;
  lastValidatedAt: string | null;
  lastErrorCode: string | null;
  accounts: { id: string; kind: 'IG_PROFESSIONAL' | 'FB_PAGE' | 'META_AD_ACCOUNT'; externalId: string; name: string | null; username: string | null }[];
  capabilities: { capabilityId: string; account: string | null; objectType: string; state: CapabilityState; apiVersion: string; checkedAt: string; nextCheckAt: string; metaErrorCode: string | null }[];
}
export interface IntegrationsResponse { metaAppConfigured: boolean; connections: Connection[] }

/** GET a read model; `reload()` refetches. `path = null` skips the request. Errors keep the server code for honest states. */
export function useApi<T>(path: string | null) {
  // Data and errors remember the path they belong to: after a path change (e.g. switching brand) the previous
  // response is never shown for the new path, not while loading and not after a failed request. A reload() of the
  // same path keeps the current data visible until the fresh response arrives.
  const [state, setState] = useState<{ path: string | null; data: T | null; error: ApiError | null }>({ path: null, data: null, error: null });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    setState((s) => (s.path === path ? { ...s, error: null } : { path, data: null, error: null }));
    api<T>('GET', path).then((d) => { if (alive) setState({ path, data: d, error: null }); }).catch((e: ApiError) => { if (alive) setState((s) => ({ path, data: s.path === path ? s.data : null, error: e })); });
    return () => { alive = false; };
  }, [path, tick]);
  const current = state.path === path;
  return { data: current ? state.data : null, error: current ? state.error : null, reload: () => setTick((t) => t + 1) };
}

export const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '') : '—');
export const fmtDay = (v: string | null | undefined) => (v ? `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}` : '—');
export const idemKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
